import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { RoomAudioRenderer, RoomContext, useTracks } from '@livekit/components-react';
import { cva } from 'class-variance-authority';
import { Track } from 'livekit-client';

import { DEXIE_IS_LOADING } from '../lib/db';
import { DEFAULT_LAYOUT_SETTINGS, loadLayoutSettings } from '../lib/layoutSettings';
import { useSingleChannelLatestConferenceTranscript } from '../lib/useSingleChannelLatestConferenceTranscript';
import { cn } from '../lib/utils';
import { isAvCallingEnabled } from '../lib/workspace';

import JoinIcon from '../../assets/images/conference-bubble-join.svg';

import { useConference } from '../contexts/ConferenceContext';
import { useRouteWorkspace } from '../contexts/RouteWorkspaceContext';
import { ConferenceWindow } from './conference/ConferenceWindow';
import { resetStoredWindowState } from './conference/useDraggableWindowPosition';
import { Flexicon } from './shared/Flexicon';
import { Tooltip } from './shared/Tooltip';

// compute current participants from actions by tracking join/leave events
const computeCurrentParticipants = (actions, allParticipants) => {
  if (!actions || !Array.isArray(actions) || actions.length === 0) {
    // fallback to all participants if actions not available
    return allParticipants || [];
  }

  // track current participant count per profile_id
  const participantCounts = {};
  const sortedEvents = [...actions].sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

  sortedEvents.forEach((event) => {
    const profileId = event.profile_id;
    if (!profileId) return;

    if (event.action === 'joined') {
      participantCounts[profileId] = (participantCounts[profileId] || 0) + 1;
    } else if (event.action === 'left') {
      participantCounts[profileId] = (participantCounts[profileId] || 0) - 1;
    }
  });

  // only include participants with net positive count (currently in call)
  const currentParticipantIds = Object.keys(participantCounts).filter((profileId) => participantCounts[profileId] > 0);

  if (!allParticipants || !Array.isArray(allParticipants)) {
    return [];
  }

  // return participants that are currently in the call
  return allParticipants.filter((participant) => currentParticipantIds.includes(participant.id));
};

const actionButtonVariants = cva('flex h-8 cursor-pointer items-center justify-center gap-2 rounded-full', {
  variants: {
    variant: {
      joinEmpty: 'w-8 bg-gray-200 hover:bg-gray-300 dark:bg-neutral-700 hover:dark:bg-neutral-700',
      joinOngoing: 'bg-blue-500 px-3 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700',
      leave: 'bg-red-600 px-3 hover:bg-red-700',
      mediaToggle: 'w-8 text-gray-300 hover:text-white',
      mediaToggleMuted: 'w-8 bg-gray-700 text-white hover:bg-gray-600 dark:bg-neutral-700 hover:dark:bg-neutral-600',
      mediaToggleScreenShare: 'rounded-lg border border-white/3 bg-gray-700 px-2 font-medium text-white hover:bg-gray-600',
      menuToggle: 'w-8 text-gray-300 hover:text-white',
      screenShareToggle: 'bg-gray-300/30 px-2 text-xs font-medium text-white hover:bg-gray-300/40',
      transcriptionToggleActive: 'w-8 bg-gray-800 hover:bg-gray-700 dark:bg-neutral-800 hover:dark:bg-neutral-700',
      transcriptionTogglePaused:
        'bg-gray-700 px-3 font-medium text-gray-200 hover:bg-gray-600 dark:bg-neutral-700 hover:dark:bg-neutral-600',
    },
  },
});

const ActionButton = forwardRef(({ variant, className, children, ...props }, ref) => {
  return (
    <button ref={ref} className={cn(actionButtonVariants({ variant }), className)} {...props}>
      {children}
    </button>
  );
});
ActionButton.displayName = 'ActionButton';

function RoomSFXRenderer() {
  // track changes to the tracks
  const tracks = useTracks([Track.Source.Camera, Track.Source.ScreenShare], { onlySubscribed: true });
  const previousTracksRef = useRef(tracks);
  const hasPlayedWelcomeChimeRef = useRef(false);

  // play welcome chime when tracks first appear, then play join/leave sounds for subsequent changes
  useEffect(() => {
    const previousTracks = previousTracksRef.current;
    const currentTracks = tracks;

    // play welcome chime once when tracks first appear (0 → >0)
    if (!hasPlayedWelcomeChimeRef.current && currentTracks.length > 0) {
      hasPlayedWelcomeChimeRef.current = true;
      window.dispatchEvent(new CustomEvent('output:internal:sfx:one-shot', { detail: { clip: 'conference-participant-joined' } }));
    } else if (hasPlayedWelcomeChimeRef.current && previousTracks.length > 0) {
      // play join/leave sounds for subsequent track changes (only if we've already played the welcome chime)
      if (currentTracks.length > previousTracks.length) {
        window.dispatchEvent(new CustomEvent('output:internal:sfx:one-shot', { detail: { clip: 'conference-participant-joined' } }));
      } else if (currentTracks.length < previousTracks.length) {
        window.dispatchEvent(new CustomEvent('output:internal:sfx:one-shot', { detail: { clip: 'conference-participant-left' } }));
      }
    }

    previousTracksRef.current = currentTracks;
  }, [tracks]);

  return null;
}

// Everything visible now lives in the call window: the faces, and the screen share that
// used to float beside them on its own drag system. What's left is the pieces with no UI.
const CallRoomRenderers = () => (
  <>
    <RoomAudioRenderer />
    <RoomSFXRenderer />
  </>
);

const bubbleWrapperVariants = cva(
  'absolute hidden h-13 min-h-0 items-center justify-center rounded-full p-3 text-xs font-medium text-white shadow-glass backdrop-blur-lg md:flex dark:text-white',
  {
    variants: {
      variant: {
        default: 'border border-white bg-gray-100/50 p-0 dark:border-white/5 dark:bg-neutral-800/80',
        joined: 'bg-gray-950/90 dark:border dark:border-white/5 dark:bg-white/15',
        error: 'bg-red-500 pr-6 dark:bg-red-700/40',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const BubbleWrapper = ({ variant, className, style, topClassName = 'top-5', children }) => {
  return (
    <div data-app-region="none" className={cn(bubbleWrapperVariants({ variant }), topClassName, className)} style={style}>
      {children}
    </div>
  );
};

// The rail width reaches the call UI as a prop where the context is in scope, and falls back to
// the persisted layout setting for callers that sit outside it.
function useSidebarWidth(sidebarWidthOverride) {
  const [storedSidebarWidth, setStoredSidebarWidth] = useState(
    () => loadLayoutSettings().sidebarWidth || DEFAULT_LAYOUT_SETTINGS.sidebarWidth,
  );

  useEffect(() => {
    if (sidebarWidthOverride != null) return;

    const handleSidebarWidthChange = (event) => {
      setStoredSidebarWidth(event.detail.width);
    };
    window.addEventListener('output:internal:sidebar-width-change', handleSidebarWidthChange);
    return () => window.removeEventListener('output:internal:sidebar-width-change', handleSidebarWidthChange);
  }, [sidebarWidthOverride]);

  return sidebarWidthOverride ?? storedSidebarWidth;
}

// The call itself: one draggable window holding the controls, the faces, and any screen
// share. Mounted ABOVE the route-scoped providers rather than inside them — those unmount
// their whole subtree while a route hydrates, and taking the <VideoTrack> elements down with
// them made every navigation flash. The room already outlived route changes; now the video
// does too.
//
// It reads only conference state, so nothing here depends on which chat is open — the window
// measures the chat column itself rather than being told how wide the rail is.
export function ConferenceCallLayer() {
  const { status, room, liveKitRoomProps, connectedChannelIsLoaded } = useConference();
  const isJoined = status === 'joined' && connectedChannelIsLoaded && !!room;

  // Every call opens at the default size and position. The window's own state is keyed by
  // channel so it survives chat switches mid-call, but rejoining the same chat produces the
  // same key — so the end of a call is what has to clear it.
  useEffect(() => {
    if (!isJoined) resetStoredWindowState();
  }, [isJoined]);

  if (!isJoined) return null;

  return (
    <div className="hidden md:block" {...liveKitRoomProps}>
      <RoomContext.Provider value={room}>
        <ConferenceWindow />
        <CallRoomRenderers />
      </RoomContext.Provider>
    </div>
  );
}

export function ConferenceBubble({
  currentChannelId,
  isInteractive,
  hidden = false,
  sidebarWidthOverride = null,
  toolbarTopClassName = 'top-5',
  hidePreJoinUi = false,
}) {
  const { workspace: currentWorkspace, workspaceId: currentWorkspaceId, profileId: currentProfileId } = useRouteWorkspace();
  const canUseAvCalling = isAvCallingEnabled(currentWorkspace);

  const sidebarWidth = useSidebarWidth(sidebarWidthOverride);

  // Only the pre-join states render from here, so this needs the call's identity and how to
  // join it — the in-call controls all moved to ConferenceWindow with the UI that used them.
  const { status, connectedChannel, joinConference } = useConference();

  // get the latest conference transcript for the current channel
  const currentConferenceTranscript = useSingleChannelLatestConferenceTranscript(connectedChannel?.id || currentChannelId);

  // compute current participants from actions to show only those currently in the call
  const currentParticipants = useMemo(
    () =>
      currentConferenceTranscript?.data?.actions && currentConferenceTranscript?.data?.participants
        ? computeCurrentParticipants(currentConferenceTranscript.data.actions, currentConferenceTranscript.data.participants)
        : currentConferenceTranscript?.data?.participants || [],
    [currentConferenceTranscript?.data?.actions, currentConferenceTranscript?.data?.participants],
  );

  // track seen conference transcript message IDs to avoid playing sound multiple times
  const seenConferenceTranscriptIdsRef = useRef(new Set());

  // listen for live conference transcript messages and play sound if conference started in the current channel
  useEffect(() => {
    const handleLiveJournalMessage = (event) => {
      const { action, data } = event.detail || {};
      if (!data || action !== 'create') return;

      // filter for conference_transcript types only
      if (data.type !== 'conference_transcript') return;

      // skip if we've already seen this message
      if (seenConferenceTranscriptIdsRef.current.has(data.id)) return;

      // always mark the message as seen, to avoid playing it multiple times
      seenConferenceTranscriptIdsRef.current.add(data.id);

      // play the sound if the conference started in the current channel
      if (data.channel_id === currentChannelId) {
        window.dispatchEvent(new CustomEvent('output:internal:sfx:one-shot', { detail: { clip: 'conference-started' } }));
      }
    };

    window.addEventListener('output:internal:live-journal:message', handleLiveJournalMessage);

    return () => {
      window.removeEventListener('output:internal:live-journal:message', handleLiveJournalMessage);
    };
  }, [currentChannelId]);

  // button handlers
  const handleJoin = useCallback(() => {
    joinConference(currentChannelId, { workspaceId: currentWorkspaceId, profileId: currentProfileId }).catch(() => {
      // Error already logged and status set to 'error' in ConferenceContext
    });
  }, [currentChannelId, currentProfileId, currentWorkspaceId, joinConference]);

  // Only the pre-join states render from here, and a screen share can't be expanded before
  // you've joined — so this no longer has a raised variant to choose between.
  const bubbleWrapperZIndex = 'z-(--stack-conference-bubble-wrapper)';

  // Joined renders from ConferenceCallLayer, which is mounted above the route providers so
  // route hydration can't tear the video down. Nothing to draw here once the call is up.
  if (status === 'joined') return null;

  // when not joined and parent requests hide (e.g., settings route), hide the bubble
  if (hidden || !currentChannelId) return null;

  // calling is on for every workspace until its admins switch it off in settings
  if (!canUseAvCalling) return null;

  // when in error state, show retry button (after hidden check so we don't show error on settings page)
  if (status === 'error') {
    return (
      <BubbleWrapper variant="error" className={bubbleWrapperZIndex} style={{ left: sidebarWidth + 20 }} topClassName={toolbarTopClassName}>
        <div className="flex items-center justify-center gap-2">
          <button
            key="retry-button"
            className="flex size-8 cursor-pointer items-center justify-center rounded-full bg-red-800/80 hover:bg-red-900 dark:bg-red-500/20 hover:dark:bg-red-600/30"
            onClick={handleJoin}
          >
            <JoinIcon className="text-white" />
          </button>
          <div key="error-message">Could not join AV. Please try again.</div>
        </div>
      </BubbleWrapper>
    );
  }

  // when not joined and channel is not interactive, hide the bubble
  if (!isInteractive) {
    return null;
  }

  if (hidePreJoinUi) return null;

  // when there's an ongoing conference in the channel, show join button with participants
  if (
    currentConferenceTranscript !== DEXIE_IS_LOADING &&
    currentConferenceTranscript?.data &&
    currentConferenceTranscript?.data?.started_at &&
    !currentConferenceTranscript?.data?.ended_at
  ) {
    return (
      <BubbleWrapper
        className={cn('flex items-center justify-center gap-2 px-2.5', bubbleWrapperZIndex)}
        style={{ left: sidebarWidth + 20 }}
        topClassName={toolbarTopClassName}
      >
        <Tooltip label="Join ongoing AV Call" placement="top">
          <ActionButton variant={'joinOngoing'} onClick={handleJoin} disabled={status === 'joining' || status === 'joined'}>
            <JoinIcon className={cn('text-white', status === 'joining' && 'animate-pulse')} />
            <span className="pr-2">Join</span>
          </ActionButton>
        </Tooltip>
        <div className="flex items-center justify-center">
          {currentParticipants?.map((participant, index) => {
            const isBehindNext = index < currentParticipants.length - 1;

            return (
              <span
                key={participant.id}
                className={cn('inline-flex', index > 0 && '-ml-3', isBehindNext && 'conference-bubble-face-stack-mask')}
              >
                <Flexicon resource={participant} size="size-8" />
              </span>
            );
          })}
        </div>
      </BubbleWrapper>
    );
  }

  // default: show empty join button
  return (
    <BubbleWrapper className={cn('w-13', bubbleWrapperZIndex)} style={{ left: sidebarWidth + 20 }} topClassName={toolbarTopClassName}>
      <Tooltip label="Start AV Call here" placement="top">
        <ActionButton variant={'joinEmpty'} onClick={handleJoin} disabled={status === 'joining' || status === 'joined'}>
          <JoinIcon className={cn('text-black dark:text-white', status === 'joining' && 'animate-pulse')} />
        </ActionButton>
      </Tooltip>
    </BubbleWrapper>
  );
}
